import { useState } from 'react';
import { Check, ChevronDown, Lock, LockOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { useUpdateEngProject, type EngProject } from '@/features/engineering/hooks';
import {
  ENG_STATUSES,
  ENG_STATUS_META,
  isEngLockedStatus,
  type EngStatus,
} from '@/features/engineering/model';
import { formatError } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { EngStatusBadge } from './EngParts';

/**
 * Status dropdown for the project header. Moving into a locked state
 * (design locked / in production) or back out of one asks for
 * confirmation, because it flips the whole file drive between
 * editable and read-only.
 */
export function EngStatusControl({ project, disabled }: { project: EngProject; disabled?: boolean }) {
  const update = useUpdateEngProject();
  const [pending, setPending] = useState<EngStatus | null>(null);

  const apply = async (status: EngStatus) => {
    try {
      await update.mutateAsync({ id: project.id, patch: { status } });
      toast.success(`Status set to ${ENG_STATUS_META[status].label}.`);
    } catch (err) {
      toast.error(formatError(err, 'Could not change the status.'));
    } finally {
      setPending(null);
    }
  };

  const choose = (status: EngStatus) => {
    if (status === project.status) return;
    const lockChanges = isEngLockedStatus(status) !== isEngLockedStatus(project.status);
    if (lockChanges) setPending(status);
    else void apply(status);
  };

  const pendingLocks = pending ? isEngLockedStatus(pending) : false;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={disabled || update.isPending} className="gap-2">
            <EngStatusBadge status={project.status} className="-ml-1" />
            <ChevronDown className="h-3.5 w-3.5 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel>Move project to…</DropdownMenuLabel>
          {ENG_STATUSES.map((status, index) => {
            const meta = ENG_STATUS_META[status];
            const current = status === project.status;
            return (
              <div key={status}>
                {index === 6 && <DropdownMenuSeparator />}
                <DropdownMenuItem onSelect={() => choose(status)} className="items-start gap-2 py-2">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                    {current ? (
                      <Check className="h-4 w-4 text-info" />
                    ) : meta.locked ? (
                      <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                    ) : null}
                  </span>
                  <span className="min-w-0">
                    <span className={cn('block text-sm', current && 'font-medium')}>{meta.label}</span>
                    <span className="block text-xs text-muted-foreground">{meta.hint}</span>
                  </span>
                </DropdownMenuItem>
              </div>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={!!pending} onOpenChange={(open) => !open && setPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {pendingLocks ? <Lock className="h-4 w-4" /> : <LockOpen className="h-4 w-4" />}
              {pendingLocks ? 'Lock the design?' : 'Reopen the design?'}
            </DialogTitle>
            <DialogDescription>
              {pendingLocks ? (
                <>
                  Moving <span className="font-medium text-foreground">{project.code}</span> to{' '}
                  <span className="font-medium text-foreground">{pending && ENG_STATUS_META[pending].label}</span>{' '}
                  freezes every file in the drive: no uploads, new versions, renames or deletions until the
                  project is moved back to an open status. Downloads, prints, costs and notes keep working.
                </>
              ) : (
                <>
                  Moving <span className="font-medium text-foreground">{project.code}</span> to{' '}
                  <span className="font-medium text-foreground">{pending && ENG_STATUS_META[pending].label}</span>{' '}
                  unfreezes the drive. Anything built from the locked design should be checked against the
                  new revisions.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPending(null)} disabled={update.isPending}>
              Cancel
            </Button>
            <Button onClick={() => pending && void apply(pending)} disabled={update.isPending}>
              {update.isPending ? 'Saving…' : pendingLocks ? 'Lock design' : 'Reopen design'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
